/**
 * Excel Add-in Types
 * Schema for representing Excel workbook content and modifications
 */

import type {
  TtfApiResponse,
  InsertTtfApiResponseOptions,
  ClearRangeClearType,
  SortRangeKey,
  SortRangeHeader,
  SortRangeOrientation,
  SortRangeMethod,
  AddExcelChartInfo,
  AddExcelChartSheetRef,
} from "./services/factset/spreadsheetIntents";
import type {
  ExcelAgentSource,
  ExcelAgentTimelineState,
} from "./agentTimeline";
import type { FinsterQueryDataSource } from "../shared/api/finster/constants";

// ============================================================================
// CELL VALUES
// ============================================================================

export type CellValueType = "string" | "number" | "boolean" | "date" | "formula" | "empty";

export interface CellValue {
  type: CellValueType;
  value: string | number | boolean | null;
  formula?: string; // Present when type is "formula"
  displayValue?: string; // Formatted display value (e.g., "$1,234.56")
}

// ============================================================================
// CELL FORMATTING
// ============================================================================

export interface FontStyle {
  name?: string; // Font family (e.g., "Calibri", "Arial")
  size?: number; // Font size in points
  color?: string; // Hex color (e.g., "#FF0000")
  bold?: boolean;
  italic?: boolean;
  underline?: "none" | "single" | "double";
  strikethrough?: boolean;
}

export interface FillStyle {
  color?: string; // Background color (hex)
  pattern?: "solid" | "none";
}

export interface BorderStyle {
  style?: "none" | "thin" | "medium" | "thick" | "dashed" | "dotted" | "double";
  color?: string; // Hex color
}

export interface CellBorders {
  top?: BorderStyle;
  bottom?: BorderStyle;
  left?: BorderStyle;
  right?: BorderStyle;
}

export interface CellAlignment {
  horizontal?: "left" | "center" | "right" | "fill" | "justify";
  vertical?: "top" | "center" | "bottom";
  wrapText?: boolean;
  indent?: number;
  textRotation?: number; // Degrees (-90 to 90)
}

export interface NumberFormat {
  format: string; // Excel number format code (e.g., "#,##0.00", "0%", "yyyy-mm-dd")
}

export interface CellFormat {
  font?: FontStyle;
  fill?: FillStyle;
  borders?: CellBorders;
  alignment?: CellAlignment;
  numberFormat?: NumberFormat;
  merge?: boolean; // Merge cells in the range
}

// ============================================================================
// CELL & RANGE
// ============================================================================

export interface Cell {
  address: string; // A1-style address (e.g., "A1", "B5")
  row: number; // 0-indexed row
  column: number; // 0-indexed column
  value: CellValue;
  format?: CellFormat;
}

/**
 * Sparse per-cell style info. Only cells with non-default formatting get
 * an entry. Keeps the JSON payload compact while giving the AI visibility
 * into headers, FDS cells, input cells, and merged regions.
 */
export interface CellStyleInfo {
  bold?: boolean;        // true if font is bold (identifies headers)
  italic?: boolean;      // true if font is italic
  fontSize?: number;     // font size in points (omitted when default)
  fontColor?: string;    // hex "#RRGGBB" (default black = omitted)
  fillColor?: string;    // hex "#RRGGBB" background (default white/none = omitted)
  underline?: boolean;   // true if any underline style is applied
  strikethrough?: boolean; // true if strikethrough
  align?: string;        // "left" | "center" | "right" (default "general" = omitted)
  merged?: boolean;      // true if cell is part of a merge
}

export interface CellRange {
  address: string; // Range address (e.g., "A1:D10")
  startRow: number;
  startColumn: number;
  rowCount: number;     // Number of rows actually included in values/formulas
  columnCount: number;
  values: (string | number | boolean | null)[][];  // 2D array of values
  formulas?: (string | null)[][];  // 2D array of formulas (null if no formula)
  numberFormats?: (string | null)[][];  // 2D array of Excel number format codes (null or omitted = "General")
  cellStyles?: Record<string, CellStyleInfo>;  // Sparse map keyed by "row,col" (0-indexed within range). Only non-default entries.
  totalRowCount?: number; // Full row count before truncation (for context)
}

// ============================================================================
// TABLE
// ============================================================================

export interface ExcelTable {
  name: string;
  address: string; // Table range (e.g., "A1:D10")
  hasHeaders: boolean;
  headers: string[];
  rows: (string | number | boolean | null)[][];
  style?: string; // Built-in table style (e.g., "TableStyleMedium2")
}

// ============================================================================
// CHART
// ============================================================================

export interface ExcelChartSeries {
  name: string;
  values: string; // Range address for values (e.g., "Sheet1!B2:B10")
  categories?: string; // Range address for categories
}

export interface ExcelChart {
  type: "bar" | "column" | "line" | "pie" | "area" | "scatter" | "doughnut";
  title?: string;
  series: ExcelChartSeries[];
  position?: {
    startRow: number;
    startColumn: number;
    endRow: number;
    endColumn: number;
  };
  /**
   * 0-based chart index on the parent sheet, surfaced from
   * `Charting.GetCharts` enrichment so the agent can target a
   * specific chart in `ChartModification.action = "update"` without
   * having to read the chart back through a separate intent. Optional
   * because Office.js mode populates this from the host-assigned
   * collection index, while older callers may leave it undefined.
   */
  index?: number;
}

// ============================================================================
// WORKSHEET
// ============================================================================

export interface ColumnInfo {
  index: number;
  width?: number; // Column width in characters
  hidden?: boolean;
}

export interface RowInfo {
  index: number;
  height?: number; // Row height in points
  hidden?: boolean;
}

export interface WorksheetContent {
  name: string;
  /**
   * Position of this sheet in the workbook. Declared **0-based** to match
   * Office.js's `worksheet.position`. Same migration caveat as
   * `WorkbookContent.activeSheetIndex` — under the FactSet bridge today
   * the wire `getWorkbookSheets[].index` is 1-based and passes through
   * unchanged via `excelService.ts::adaptWorkbookSheets`, which is a
   * silent off-by-one against Office.js mode. FactSet's pre-announced
   * 0-based migration closes that. See
   * `f2_docs/excel/finster-docs/missing-intents-v0.md`
   * → `#open-zero-index-migration`.
   */
  index: number;
  usedRange?: CellRange;
  /**
   * Excel-native worksheet visibility. Populated when the bridge-mode
   * extract asks `GetWorkbookSheets` for the `visibility` property
   * (see `excelService.ts::adaptWorkbookSheets`); `undefined` on
   * older hosts that don't honour `options.properties` and in the
   * Office.js path (we don't probe for visibility there today).
   * Hidden / very-hidden sheets are still included in the extract
   * so prompts can reason about them, but the agent can elect to
   * skip writes against them using this flag.
   */
  visibility?: "Visible" | "Hidden" | "VeryHidden";
  tables?: ExcelTable[];
  charts?: ExcelChart[];
  columns?: ColumnInfo[];
  rows?: RowInfo[];
}

// ============================================================================
// WORKBOOK (Current State)
// ============================================================================

/**
 * One entry in `WorkbookContent.openWorkbooks`. Mirrors the captured
 * payload from `factset.Spreadsheet.GetOpenWorkbooks` (see
 * `excel-docs/intent-details.md` §"Get Open Workbooks"). All fields
 * are optional because the wire payload is example-level — readers
 * should narrow defensively. Empty/unsaved workbooks have `path === ""`
 * and `fullName === name`.
 *
 * **Canonical site.** Re-exported from
 * `services/factset/spreadsheetIntents.ts` so the bridge wrapper that
 * produces this shape and the domain consumer that stores it
 * (`WorkbookContent`) reference the same declaration.
 */
export interface OpenWorkbookInfo {
  name?: string;
  path?: string;
  fullName?: string;
  saved?: boolean;
  readOnly?: boolean;
  isActive?: boolean;
}

/**
 * One workbook external-data connection (e.g. a FactSet feed). The
 * `factset.Spreadsheet.GetWorkbookConnections` response is **typed**
 * on the wire; the only captured sample shows `connections: []` so
 * the per-element field set isn't documented yet (see
 * `intent-details.md` → "Get Workbook Connections"). Until a
 * non-empty payload lands, callers narrow defensively at the use
 * site — that's a docs gap, not a typing gap.
 *
 * **Canonical site.** Re-exported from
 * `services/factset/spreadsheetIntents.ts` (see `OpenWorkbookInfo`).
 */
export type WorkbookConnection = Record<string, unknown>;

/**
 * One named range / named formula entry. The
 * `factset.Spreadsheet.GetWorkbookNamedObjects` response is typed on
 * the wire; no response sample has been captured in
 * `intent-details.md` yet, so the per-element field set is still
 * example-pending. Adapters narrow defensively at the use site.
 *
 * **Canonical site.** Re-exported from
 * `services/factset/spreadsheetIntents.ts` (see `OpenWorkbookInfo`).
 */
export type WorkbookNamedObject = Record<string, unknown>;

export interface WorkbookContent {
  /**
   * Position of the active sheet in the workbook. Declared **0-based**
   * (matching Office.js's `worksheet.position`).
   *
   * **Indexing caveat (will resolve when FactSet's pre-announced migration
   * lands).** Under the FactSet bridge today the value is populated from
   * the wire `GetActiveExcelContext.activeSheet.index`, which is
   * **1-based**. The bridge passes that through unchanged, so a bridge-mode
   * `activeSheetIndex` of `1` and an Office.js-mode value of `0` both mean
   * "first sheet" — a silent off-by-one between the two modes. FactSet has
   * pre-announced a 0-based migration for `getActiveExcelContext`,
   * `getWorkbookSheets`, `addWorksheet.options.index`, `getTables`, and
   * `getCharts`; that eliminates the disagreement and the bridge value
   * starts matching the field's declared semantics. See
   * `f2_docs/excel/finster-docs/missing-intents-v0.md`
   * → `#open-zero-index-migration`.
   */
  activeSheetIndex: number;
  activeSheetName: string;
  sheets: WorksheetContent[];

  /**
   * Active workbook name as reported by the bridge-mode
   * `GetActiveExcelContext` intent (typed payload, see
   * `missing-intents-v0.md` §0). Useful when the agent needs to
   * disambiguate cross-workbook references — pair with `openWorkbooks`
   * for the full set. Undefined under Office.js or when the intent
   * fails.
   */
  workbookName?: string;

  /**
   * Cursor / selection context at read time. Populated best-effort by the
   * read pipeline so the model can answer "what cell am I on?" / "what
   * range did I select?" without an extra round trip, and so agent
   * steps can target "the cell I'm on" when the user uses
   * deictic phrasing. Both are bare A1 addresses (no sheet prefix today
   * — see `missing-intents-v0.md` §8.1) and may be undefined when the
   * underlying intent (`GetActiveCellAddress` /
   * `GetSelectedAddressRange`) fails or no selection exists. The
   * extractor swallows failures to `console.warn` so a slow/failing
   * cursor read never blocks the primary read.
   */
  activeCellAddress?: string;
  selectedRangeAddress?: string;

  /**
   * Host application activity state from the `GetActiveExcelContext`
   * payload. Either flag being `true` is a signal that the next
   * write may be ignored / queued / blocked: the agent can use
   * them as a "should I retry / wait?" hint. Both are undefined in
   * Office.js mode and when the intent fails.
   */
  isBusy?: boolean;
  isInEditMode?: boolean;

  /**
   * Workbook-level enrichment populated by the bridge-mode read path.
   * Each field is best-effort — the extractor swallows failures to
   * `console.warn` so a slow/failing introspection intent never blocks
   * the primary read. Office.js mode leaves these undefined today.
   *
   * **Per-workbook cache (AI-2616).** These three fields are served
   * from an in-process cache keyed by `workbookName` after the first
   * fetch — FactSet flagged the previous "3 RPCs per prompt" pattern
   * as wasteful. Cache invalidation:
   * - `namedObjects` slot is dropped after our agent applies
   *   `addNamedObject` / `removeNamedObject`, so the next prompt
   *   reflects the mutation.
   * - `connections` / `openWorkbooks` have no agent-side mutation
   *   surface and stay cached for the session.
   * - Workbook switch (new `workbookName`) implicitly drops the slot.
   * - UI-driven ribbon changes by the user (Formulas → Define Name,
   *   adding a data connection, opening/closing a workbook) are NOT
   *   captured — the user can reload the addin or switch workbooks to
   *   refresh. Acceptable for the bug's urgency profile; revisit if
   *   complaints surface.
   *
   * See `enrichWorkbookContentViaBridge` in `excelService.ts` for the
   * cache module banner and the read / upsert / invalidate helpers.
   */
  openWorkbooks?: OpenWorkbookInfo[];
  connections?: WorkbookConnection[];
  namedObjects?: WorkbookNamedObject[];
}

// ============================================================================
// MODIFICATIONS (AI Response Schema)
// ============================================================================

export interface CellModification {
  address: string; // Single cell (e.g., "A1") or range (e.g., "A1:C5")
  action: "set" | "clear" | "format";

  // For "set" action
  value?: string | number | boolean | null;
  formula?: string; // Excel formula (e.g., "=SUM(A1:A10)")
  /**
   * Exact IDs returned by the research tool for this cell's sourced content.
   * The executor resolves them into a deterministic Note before host apply;
   * IDs are never persisted in the workbook.
   */
  citationIds?: string[];

  // For "format" action or optional formatting with "set"
  format?: CellFormat;

  // Optional comment to add to the cell
  comment?: string;

  /**
   * Legacy dual-emit path — natural-language description of what this
   * FDS-bound cell should fetch. Emitted only by compatibility callers
   * that still produce cells whose `formula` starts with `=FDS(`,
   * `=FDSC(`, or `=FDSZ(`. Consumed by
   * `applyWorkbookModifications`: when the user has FactSet OAuth +
   * the TTF scope and the cell-write loop is on the intent-bridge
   * path, the runtime calls FactSet's TTF API with this query and
   * writes the resolved formula instead of writing `formula` directly.
   * Any failure mode (gate closed, TTF timeout, upstream error,
   * malformed shape) falls back to the existing cell-write path so the
   * supplied `formula` still lands. Tool-mode `generate_formula` calls
   * intentionally do not populate this field.
   *
   * Range-shaped writes (`address` containing `:`) are NOT eligible —
   * TTF is one-query-per-cell, so range fills always use `formula`.
   */
  ttfQuery?: string;
}

export interface RangeModification {
  address: string; // Range address (e.g., "A1:D10") — for "moveRange" this is the SOURCE
  action:
    | "fill"
    | "clear"
    | "format"
    | "sort"
    | "filter"
    | "autofit"
    | "resize"
    | "deleteRange"
    | "insertRange"
    | "moveRange"
    | "hideColumns"
    | "hideRows"
    | "showColumns"
    | "showRows"
    | "groupColumns"
    | "groupRows"
    | "ungroupColumns"
    | "ungroupRows"
    | "select";
  // Visibility actions:
  //  - `hideColumns` / `showColumns` operate on column-shaped addresses
  //    (e.g. "A:A", "B:D")
  //  - `hideRows` / `showRows` operate on row-shaped addresses
  //    (e.g. "5:5", "5:10")
  // Cell-shaped addresses are rejected at apply time.
  //
  // Outline grouping actions:
  //  - `groupColumns` / `ungroupColumns` operate on column-shaped
  //    addresses (`"A:C"`, `"$A:$C"`).
  //  - `groupRows` / `ungroupRows` operate on row-shaped addresses
  //    (`"5:10"`, `"$5:$10"`).
  // Same shape-rejection contract as the visibility actions. Nested
  // outline-level support is host-side only — the v0 schema collapses
  // to "group these rows / columns" (see `#open-outline-levels` in
  // `missing-intents-v0.md` §6 for the follow-up).

  // For "fill" action - populate entire range with values
  values?: (string | number | boolean | null)[][];
  formulas?: (string | null)[][];
  /**
   * Row-major citation IDs aligned with `values` / `formulas`. Use `null`
   * for an uncited coordinate and a non-empty ID array for a sourced cell.
   */
  citationIdsMatrix?: Array<Array<string[] | null>>;

  // For "format" action
  format?: CellFormat;

  // For "clear" action — pure in-place clear via
  // `factset.Spreadsheet.ClearRange`. Distinct from `deleteRange` which
  // shifts. `clear.clearType` defaults to the host's `"All"` when omitted.
  clear?: {
    clearType?: ClearRangeClearType;
  };

  // For "sort" action — multi-key sort via `factset.Spreadsheet.SortRange`.
  // `sortKeys[].key` is a cell reference inside the sort column or row
  // (e.g. `"B1"` for the first column of `A1:D20`); 1–3 entries per call.
  // `sort.options` exposes the host's `header` / `matchCase` /
  // `orientation` / `sortMethod` knobs verbatim.
  sort?: {
    sortKeys: SortRangeKey[];
    options?: {
      header?: SortRangeHeader;
      matchCase?: boolean;
      orientation?: SortRangeOrientation;
      sortMethod?: SortRangeMethod;
    };
  };

  // For "autofit" — fit row/column to content (no explicit number).
  fitColumns?: boolean;
  fitRows?: boolean;

  // For "resize" — set an explicit size in the host's native unit. Use
  // `width` for a column-shaped range (e.g. "A:A", "B:D"); use `height`
  // for a row-shaped range (e.g. "5:5", "1:3"). Maps to the host's
  // `factset.spreadsheet.resizeExcelElements` with mode `ABSOLUTE`.
  width?: number;
  height?: number;

  // For visibility actions (`hideColumns` / `showColumns` / `hideRows` /
  // `showRows`) — `address` MUST be column-shaped (`"A:A"`, `"B:D"`) for
  // columns, or row-shaped (`"5:5"`, `"1:3"`) for rows. Cell-shaped
  // addresses are rejected at the call site (see
  // `excelService.ts::applyRangeModificationViaIntents`) and surface a
  // warn-skip.

  // For "select" — pure cursor / UX action that moves the host's active
  // selection to `address` without writing data. Maps to the host's
  // `factset.Spreadsheet.SelectRange` intent (which routes through the
  // `OI Remote Iframe` relay; the wrapper handles the appId override).
  // Use to land the user on cells the assistant just touched, or as a
  // standalone "show me / highlight A1:C10" verb. No extra payload —
  // sheet targeting comes from the enclosing `WorksheetModification.sheetName`.

  // For "deleteRange" action — direction to shift surrounding cells.
  // Maps to the host's `factset.spreadsheet.deleteRange.shift` enum
  // (only the `Shift*` variants; `EntireRow`/`EntireColumn` are not yet
  // surfaced on the Finster agent side).
  deleteRange?: {
    shiftDirection?: "left" | "up";
  };

  // For "insertRange" action — direction to shift existing cells, plus
  // optional formatting source. Maps to the host's
  // `factset.spreadsheet.insertRange.{shift,copyOrigin}`.
  insertRange?: {
    shiftDirection?: "right" | "down";
    copyOrigin?: "leftOrAbove" | "rightOrBelow";
  };

  // For "moveRange" action — `address` (above) is the SOURCE range; this
  // sub-object carries the destination top-left anchor (e.g. `"D1"`) and
  // an optional `preserveSource` flag (`true` = copy semantics, `false` /
  // omitted = cut-and-paste). Maps to the host's
  // `factset.spreadsheet.moveRange.{source,destination,options.preserveSource}`.
  // Cross-sheet moves are supported via optional `sourceSheetName` /
  // `destinationSheetName`; omit to operate on the active sheet.
  moveRange?: {
    destination: string;
    preserveSource?: boolean;
    sourceSheetName?: string;
    destinationSheetName?: string;
  };

  // Optional comment to add to the first cell of the range
  comment?: string;
}

export interface TableModification {
  action: "create" | "delete" | "update" | "addRow" | "addColumn";
  name?: string; // For delete/update

  // For "create"
  address?: string; // Where to create the table (e.g., "A1")
  headers?: string[];
  rows?: (string | number | boolean | null)[][];
  style?: string;

  // For "addRow" / "addColumn"
  data?: (string | number | boolean | null)[];
  index?: number; // Where to insert (default: end)
}

/**
 * Friendly per-series payload used by `ChartModification.action =
 * "addSeries"`. Mirrors the wire `series` shape that
 * `factset.Spreadsheet.Charting.AddSeriesToChart` expects, with two
 * agent-friendly niceties translated at the bridge boundary:
 *
 *  - `chartType` is the agent-facing friendly enum (`"column"` / `"line"`
 *    / etc.), mapped to the wire `xl*` enum the same way the create
 *    path does.
 *  - `axisGroup` is `"primary"` / `"secondary"`, mapped to
 *    `"xlPrimary"` / `"xlSecondary"` on the wire.
 *
 * Everything else is passed through verbatim. Use `Sheet1!$A$2:$A$10`-
 * style fully-qualified ranges for `values` and `categories` so the
 * series binds correctly when added on a different sheet from the
 * data source.
 */
export interface ChartSeriesPayload {
  /** Required: A1 range for the Y-values; sheet prefix preferred. */
  values: string;
  /** A1 range for the X-axis categories. */
  categories?: string;
  /** Series display name (legend label). */
  name?: string;
  /** Per-series chart type for combo charts. */
  chartType?: "bar" | "column" | "line" | "pie" | "area" | "scatter" | "doughnut";
  /** Which axis group to plot on. */
  axisGroup?: "primary" | "secondary";
  /** Solid fill color, hex `"#RRGGBB"`. */
  fillColor?: string;
  /** Line / border color, hex `"#RRGGBB"`. */
  lineColor?: string;
  /** Line weight in points. */
  lineWeight?: number;
}

export interface ChartModification {
  action: "create" | "delete" | "update" | "addSeries" | "updateSeries" | "removeSeries";

  // For "create" (native Excel chart via Charting.AddExcelChart)
  type?: "bar" | "column" | "line" | "pie" | "area" | "scatter" | "doughnut";
  /**
   * Wire-level Excel chart enum, e.g. `"xlColumnClustered"` or `"xlLine"`.
   * Prefer this when the LLM/tooling already knows the FactSet
   * `AddExcelChart` contract. If omitted, the runtime maps the friendly
   * `type` field above to a conservative `xl*` default.
   */
  chartType?: string;
  title?: string;
  dataRange?: string; // Data source range (legacy alias, e.g. "A1:D10")
  sourceRange?: string; // Data source range (FactSet AddExcelChart field)
  /**
   * Explicit chart target. Usually omitted in agent-authored JSON because the
   * enclosing `WorksheetModification.sheetName` resolves to `sheetRef.name`.
   * Exposed so callers can mirror `factset.spreadsheet.charting.addExcelChart`
   * directly, including `sheetRef.index`.
   */
  sheetRef?: AddExcelChartSheetRef;
  /**
   * Target workbook for direct chart calls. Agent runs normally inherit the
   * pinned workbook from the apply options; this field is useful for direct
   * callers/tests that mirror the host intent options.
   */
  workbookName?: string;
  position?: {
    /**
     * Legacy cell-anchor position: top-left cell for the chart (e.g. "F2").
     * When present without `top`/`left`, width/height are interpreted in
     * approximate cell units and translated to points.
     */
    startCell?: string;
    /**
     * Direct AddExcelChart position in Excel points. When `top`, `left`,
     * `width`, and `height` are all present, they are passed through as the
     * host's `options.position` verbatim.
     */
    top?: number;
    left?: number;
    width?: number;
    height?: number;
  };
  /**
   * Additional chart-level properties accepted by
   * `Charting.AddExcelChart` / `Charting.UpdateExcelChart` (title, legend,
   * axes, chart area, plot area, etc.). Series changes must use the dedicated
   * series actions below.
   */
  chartInfo?: AddExcelChartInfo;

  /**
   * 0-based chart index on the parent sheet for `update`,
   * `addSeries`, `updateSeries`, `removeSeries`, and the future
   * `delete`. Sourced from the agent-visible
   * `WorksheetContent.charts[].index` field, which is itself
   * populated from `Charting.GetCharts` enrichment in bridge mode and
   * from `chart.position` order in Office.js mode. Optional — when
   * omitted, both paths default to the first chart on the sheet.
   *
   * `type` and `title` are reused for `update`: setting them changes
   * the existing chart's chart-type enum and title text. Series writes
   * use the dedicated `series` / `seriesIndex` fields below; they are
   * NOT applied via `update`.
   */
  chartIndex?: number;

  /**
   * For `action ∈ { "updateSeries", "removeSeries" }`: the 0-based
   * slot of the series within the chart's series collection.
   * Sourced from the host's `Charting.GetCharts` series rows, or
   * captured from the `seriesIndex` returned by an earlier
   * `addSeries` apply. Required for those two actions; ignored
   * otherwise. Removing a series shifts every later index down by
   * one — when chaining multiple `removeSeries` mods on the same
   * chart, emit them in DESCENDING `seriesIndex` order so earlier
   * indices stay stable across the batch.
   */
  seriesIndex?: number;

  /**
   * For `action = "addSeries"`: the new series to append to the chart
   * identified by `chartIndex`. `values` is the only required field.
   *
   * For `action = "updateSeries"`: a partial body — every field
   * including `values` is optional, and the host applies only the
   * fields that are present (the response's `appliedProperties[]`
   * confirms which keys actually landed). Setting a field to a
   * different value replaces the prior value; there is no "clear"
   * sentinel today, so to e.g. drop a custom marker style you'd need
   * to set it to `xlMarkerStyleNone` rather than omit it.
   *
   * Ignored for `removeSeries`. See `ChartSeriesPayload` for the
   * friendly→wire translation that runs at the bridge boundary.
   */
  series?: ChartSeriesPayload;

}

/**
 * One FactSet ActiveGraph (FDSChart OLE) embed to drop on a sheet.
 *
 *  - `action: "create"` → `factset.Spreadsheet.AddActiveGraph`. Creates
 *    a new OLE shell at the given coords (or the wrapper's defaults:
 *    top=100, left=5, width=320, height=200 — compact landscape; the chat-
 *    chart applier swaps in a near-square 260x220 for pie / segment-
 *    breakdown FactSet types via `chartCoordsForPayload`) and binds
 *    the FDSChart XML inside.
 *
 * **Agent-emittable actions are limited to `"create"` today.**
 * In-place XML replacement on the currently-selected ActiveGraph (the
 * underlying `factset.Charting.SetActiveGraphXml` intent) is wired
 * Finster-side but intentionally not exposed to the agent — the
 * surface is too immature for round-trip mutations. The dispatcher /
 * wrapper / dev-tools dialog remain available for non-agent flows; the
 * applier branch is structurally retained behind a defensive
 * `trackSkip` for any caller that bypasses the type narrowing.
 *
 * Use this collection when the chart is "FactSet-rendered, FactSet-
 * controlled" — i.e. the styling, formulas, and refresh behaviour come
 * from FDSChart itself. For a static, portable native Excel chart drawn
 * from a workbook range, use `WorksheetModification.charts[]` with
 * `action: "create"` + `dataRange` instead.
 *
 * Delete / move / resize for ActiveGraph objects are part of FactSet's
 * pre-announced shape-ops roadmap (see `missing-intents-v0.md`
 * `#open-shape-ops`).
 */
export interface ActiveGraphModification {
  /**
   * - `"create"` inserts a new ActiveGraph at `position` (or defaults).
   *
   * The previous `"update-selected"` literal (which targeted
   * `factset.Charting.SetActiveGraphXml`) has been removed from the
   * agent-emittable union — the underlying intent is too immature
   * to expose to the LLM. The applier retains a runtime guard for
   * unknown action values.
   */
  action: "create";

  /**
   * The FDSChart XML body. Required, non-empty.
   *
   * Pass the bare `<FDSChart …>…</FDSChart>` content the host returned
   * (e.g. via `factset.PreviewChart`'s `fdsChartXml` field), or a
   * wire-level envelope string that already starts with `COORD_TOP=` —
   * the bridge wrapper sniffs both shapes and only re-wraps the bare
   * form.
   */
  activeGraphContent: string;

  /**
   * Optional anchor / size override for the ActiveGraph OLE shape, in
   * the host's pixel-ish units relative to the active sheet's
   * top-left. Defaults are top=100, left=5, width=320, height=200 (an
   * 8:5 landscape chart anchored just below the column headers — matches
   * `DEFAULT_ACTIVE_GRAPH_COORDS` in the FactSet intent bridge).
   */
  position?: {
    top?: number;
    left?: number;
    width?: number;
    height?: number;
  };

  /**
   * Whether the host should rebuild / refresh data on the chart after
   * the XML is applied. Defaults to `true`. Only meaningful for the
   * (currently agent-hidden) in-place XML replacement path.
   */
  refresh?: boolean;

  /**
   * Optional human-friendly title for log lines and skip messages. Not
   * passed to the host (the host derives the chart title from the XML
   * body itself); this is purely an agent-side label.
   */
  title?: string;
}

/**
 * One sheet-level structural change. Each action dispatches to a
 * dedicated host intent (no Office.js fallback in bridge mode):
 *
 *  - `create`        → `factset.Spreadsheet.AddWorksheet` with optional
 *    `options.worksheetName`. The host applies Excel naming rules
 *    (sanitisation, 31-char cap) — read back via `getWorkbookSheets`
 *    if the applied name matters.
 *  - `delete`        → `factset.Spreadsheet.DeleteWorksheet` (with
 *    `displayAlerts: false` so the host doesn't block on a modal).
 *  - `rename`        → `factset.Spreadsheet.RenameWorksheet`.
 *  - `activate`      → `factset.Spreadsheet.ActivateWorksheet`.
 *  - `move`          → `factset.Spreadsheet.MoveSheet` (see the `move`
 *    sub-object below).
 *  - `setVisibility` → `factset.Spreadsheet.SetWorksheetVisibility`
 *    (cataloged 2026-05-12, PR #55; `worksheetName` made optional
 *    host-side 2026-05-12). `visibility` is required and takes one of
 *    `"Visible" | "Hidden" | "VeryHidden"`. `name` is optional for
 *    this action — omit to flip the active worksheet's visibility.
 *    `"VeryHidden"` removes the sheet from the Excel UI entirely;
 *    only another programmatic `setVisibility` → `"Visible"` brings
 *    it back.
 *
 * `name` is the **target** sheet for `delete` / `rename` / `activate` /
 * `move` / `setVisibility` and the **requested** name for `create`.
 * `setVisibility` accepts `name` as optional (active-sheet default);
 * the other actions still require it. `newName` is the new name for
 * `rename`.
 */
export interface SheetModification {
  action:
    | "create"
    | "delete"
    | "rename"
    | "activate"
    | "move"
    | "setVisibility";
  name?: string; // Target sheet. Required for delete/rename/activate/move; optional for create (host picks a default) and setVisibility (defaults to active worksheet, host change 2026-05-12).
  newName?: string; // For rename

  /**
   * Required for `action: "setVisibility"`. Excel-native visibility
   * state, accepted verbatim by the host.
   *
   *  - `"Visible"`    — sheet visible in tabs and ribbon.
   *  - `"Hidden"`     — hidden, but user can right-click → Unhide.
   *  - `"VeryHidden"` — hidden, **not** exposed in the Unhide list;
   *    only another programmatic `setVisibility` → `"Visible"` brings
   *    it back. Use for "internal" sheets the user shouldn't toggle.
   */
  visibility?: "Visible" | "Hidden" | "VeryHidden";

  // For "move" action — reorder a sheet within its workbook (or move it
  // into a different open workbook). Maps to the host's
  // `factset.spreadsheet.moveSheet`.
  //
  //  - `newIndex` is **0-based** on the destination workbook (e.g. `0`
  //    puts the sheet at the leftmost tab). Required.
  //  - `sourceWorkbookName` / `targetWorkbookName` default to the active
  //    workbook on each side. Set both (and to different values) for a
  //    cross-workbook move.
  //  - `selectSheet: true` activates the moved sheet at its new home.
  //
  // The wire `options.worksheetName` is sourced from the parent
  // `SheetModification.name` field (existing convention for "target
  // sheet" across delete/rename/activate).
  move?: {
    newIndex: number;
    sourceWorkbookName?: string;
    targetWorkbookName?: string;
    selectSheet?: boolean;
  };
}

export interface LogoModification {
  companyName: string; // Company name to fetch logo for
  address: string; // Cell address where logo should be placed
  width?: number; // Width in pixels (default 60)
  height?: number; // Height in pixels (default 60)
}

export interface WorksheetModification {
  sheetName?: string; // Target sheet (default: active sheet)
  cells?: CellModification[];
  ranges?: RangeModification[];
  tables?: TableModification[];
  charts?: ChartModification[];
  logos?: LogoModification[]; // Company logos to insert

  /**
   * FactSet ActiveGraph (FDSChart OLE) operations. Bridge-only —
   * Office.js mode warn-skips the entire collection (no native
   * equivalent; the OLE control is a FactSet-only surface). Only one
   * agent-emittable action today:
   *  - `"create"` → `factset.Spreadsheet.AddActiveGraph`
   *
   * The `"update-selected"` variant (in-place XML replacement via
   * `factset.Charting.SetActiveGraphXml`) is intentionally NOT exposed
   * to the agent: the surface is too immature for round-trip
   * mutations. The dispatcher / wrapper remain wired for dev-tools /
   * non-agent flows only. See `ActiveGraphModification` for the
   * per-entry shape.
   *
   * Distinct from `charts[]` (which produces a native Excel chart from
   * a workbook range): use this collection when the LLM has FDSChart
   * XML in hand (cached from a previous `factset.PreviewChart`
   * round-trip, or hand-rolled).
   */
  activeGraphs?: ActiveGraphModification[];

  /**
   * Generic shape operations (charts addressed by host id, ActiveGraph
   * OLE embeds, native Excel charts, images) — delete / modify /
   * select. Bridge-only — Office.js mode warn-skips because the host
   * addresses shapes by an opaque numeric id and there's no Office.js
   * round-trip that produces those ids today. See
   * `ShapeModification` for the per-entry shape; the bridge applier
   * dispatches each entry through
   * `excelHostOps::{deleteShape,modifyShapes,selectShapes}`.
   *
   * Distinct from `charts[]` (agent-driven Excel chart writes
   * keyed on `chartName`) and `activeGraphs[]` (FDSChart OLE create /
   * update with raw XML). This collection is the gap-closer for
   * delete / move / resize / select on the shape surface that
   * §4.3 of `missing-intents-v0.md` previously warn-skipped as
   * "Chart action ... not supported by intent bridge yet".
   */
  shapes?: ShapeModification[];

  /**
   * Freeze or unfreeze panes on this worksheet. **Bridge-only** —
   * dispatched through `factset.Spreadsheet.FreezeUnfreezePanes`. The
   * Office.js path warn-skips this slot (see the
   * `applyWorksheetModificationOfficeJs` freeze-panes block for why
   * we don't carry a duplicate fallback — `Worksheet.freezePanes`
   * only holds a single freeze state per sheet and the workarounds
   * carry footguns; freeze-pane state is ergonomic rather than
   * data-correctness-bearing, so it's an acceptable drop on the
   * non-bridge path). Mirrors the host's intent shape:
   *   - `mode: "Freeze"` requires at least one of `row` / `column`
   *     (a zero of both would freeze nothing).
   *   - `mode: "Unfreeze"` ignores `row` / `column` — the host clears
   *     any existing freeze regardless.
   * `worksheetName` / `workbookName` default to the active surfaces;
   * pass them to target a non-active sheet. Applied once per sheet,
   * before the per-collection cell / range / chart writes.
   */
  freezePanes?: FreezePanesModification;
}

/**
 * Generic shape operation entry for `WorksheetModification.shapes[]`.
 *
 *  - `action: "delete"` removes the shape via
 *    `factset.Spreadsheet.DeleteShape`. Only `id` is required.
 *  - `action: "modify"` moves and/or resizes the shape via
 *    `factset.Spreadsheet.ModifyShapes`. All four of `top` / `left` /
 *    `height` / `width` are optional individually — pass only the
 *    ones you want to change. Units are Excel **points**, matching
 *    `AddExcelChart.position` (1pt = 1/72 inch).
 *  - `action: "select"` updates the host's active shape selection via
 *    `factset.Spreadsheet.SelectShapes`. No-op on data; pure cursor /
 *    UX action. Multi-select by emitting one entry per id and the
 *    bridge applier batches them into a single call when possible.
 *
 * `sheetName` is forwarded to the host for cross-sheet targeting where
 * the underlying intent supports it (`modify` / `select`); the host's
 * `deleteShape` capture omits `sheetName` so we only send it when
 * set (see `spreadsheetIntents.ts::deleteShapeViaIntents` for the
 * forward-compat treatment).
 */
export interface ShapeModification {
  action: "delete" | "modify" | "select";
  id: number; // Host-assigned numeric id (returned by chart-family writes)
  sheetName?: string;

  // For "modify" — every field is independently optional.
  top?: number;
  left?: number;
  height?: number;
  width?: number;
}

/**
 * Freeze-panes modification entry on `WorksheetModification.freezePanes`.
 *
 * `row` / `column` indices are the row/column AT which to freeze (the
 * frozen pane covers everything before the given index). Indexing
 * basis is host-side (0- vs 1-based has not yet been captured
 * end-to-end — verify on first apply and update this doc).
 */
export interface FreezePanesModification {
  mode: "Freeze" | "Unfreeze";
  row?: number;
  column?: number;
  worksheetName?: string;
  workbookName?: string;
}

/**
 * Workbook-level mutation that doesn't fit the per-sheet
 * `WorksheetModification` shape: adding a whole new workbook, copying a
 * worksheet (which can land in a different workbook), or inserting
 * sheets from a base64-encoded file. Each entry maps 1:1 to a
 * `factset.Spreadsheet.*` intent — see
 * `excelService.ts::applyWorkbookOperationViaIntents`.
 */
export interface WorkbookOperation {
  action:
    | "addWorkbook"
    | "copyWorksheet"
    | "insertWorksheetsFromBase64"
    | "recalculate"
    | "refreshFactSetData"
    | "refreshCalculate"
    | "getRefreshStatus"
    | "getRefresh"
    | "awaitRefreshCalculate"
    | "addNamedObject"
    | "removeNamedObject"
    | "search"
    | "insertTtfApiResponse";

  // For "copyWorksheet" — both fields are optional. When omitted, the
  // host copies the active sheet with its default placement.
  copyWorksheet?: {
    sourceSheetName?: string;
    location?: "before" | "after";
  };

  // For "insertWorksheetsFromBase64" — `base64` is required (the workbook
  // file body, base64-encoded). `addWorkbook` mirrors the host's
  // `addWorkbook.{enabled,name}` toggle.
  insertWorksheetsFromBase64?: {
    base64: string;
    addWorkbook?: {
      enabled?: boolean;
      name?: string;
    };
  };

  // For "recalculate" — locally re-evaluate workbook formulas via
  // `factset.Spreadsheet.ApplicationCalculate`. Does NOT re-fetch FDS data
  // over the network; for that, use "refreshFactSetData". Default
  // `recalculate` (only changed cells); `full` recomputes every cell;
  // `fullRebuild` also rebuilds the dependency graph (slowest, most
  // thorough). Use the default unless the user asked for "full" or
  // "rebuild".
  recalculate?: {
    mode?: "recalculate" | "full" | "fullRebuild";
  };

  // For "refreshCalculate" — kick off async template instantiation via
  // `factset.RefreshCalculate`. Returns a refresh job id; use
  // "getRefreshStatus" / "awaitRefreshCalculate" to poll completion.
  refreshCalculate?: {
    key: string;
    // Host expects strings on the wire today; the runtime coerces
    // numbers/booleans to strings defensively.
    inputs: Record<string, string | number | boolean>;
    timeoutMs?: number;
  };

  // For "getRefreshStatus" — one-shot poll of a prior refresh job.
  // If `refreshId` is omitted, the runtime uses the most recent id
  // returned by a preceding "refreshCalculate" operation in the same
  // `workbookOperations` batch.
  getRefreshStatus?: {
    refreshId?: string;
    timeoutMs?: number;
  };

  // For "getRefresh" — fetch the refresh result payload for a prior
  // refresh job. If `refreshId` is omitted, runtime falls back to the
  // most recent id from a preceding `refreshCalculate` in the same
  // `workbookOperations` batch.
  getRefresh?: {
    refreshId?: string;
    // Optional Office-refresh URL override used by some host builds.
    orUrl?: string;
    timeoutMs?: number;
  };

  // For "awaitRefreshCalculate" — poll until terminal status.
  // `refreshId` resolution is the same as "getRefreshStatus".
  awaitRefreshCalculate?: {
    refreshId?: string;
    intervalMs?: number;
    timeoutMs?: number;
    pollTimeoutMs?: number;
  };

  // For "addNamedObject" — create a workbook-level named range/formula.
  // `name` is required. Supply either:
  //  - `refersTo`: a name formula/address expression (e.g. "Sheet1!$B$2:$B$10"),
  //  - OR `address`: a plain A1 range string (convenience; coerced to
  //    `refersTo` by the dispatcher when `refersTo` is absent).
  addNamedObject?: {
    name: string;
    refersTo?: string;
    address?: string;
    nameType?: "Range" | "Formula" | string;
    workbookName?: string;
    scope?: number;
    isVisible?: boolean;
    // Back-compat alias for older generated payloads.
    visible?: boolean;
    comment?: string;
  };

  // For "removeNamedObject" — delete a workbook-level named range/formula.
  removeNamedObject?: {
    name: string;
    workbookName?: string;
    scope?: number;
  };

  // For "search" — run the host's workbook search primitive.
  // This is a read-only operation (no workbook mutation).
  search?: {
    query: string;
    workbookName?: string;
    worksheetName?: string;
    matchCase?: boolean;
    matchWholeCell?: boolean;
    searchIn?: string;
    maxResults?: number;
  };

  // For "insertTtfApiResponse" — wired in Phase 4. The primary
  // producer is the per-cell TTF dispatcher in
  // `applyWorkbookModificationsViaIntents` (cell-write loop), which
  // builds the `apiResponse` in-process via
  // `services/factset/ttfCellDispatch.ts` and writes through the same
  // host wrapper. This workbook-operations slot stays available for
  // the (currently rare) path where a producer hands a pre-built
  // `apiResponse` envelope through the workbook-operations channel
  // directly. Both routes terminate in
  // `excelHostOps.insertTtfApiResponse`.
  insertTtfApiResponse?: {
    apiResponse: TtfApiResponse;
    options?: InsertTtfApiResponseOptions;
  };
}

/**
 * Explicit internal cell-annotation mutation used by the dedicated agent
 * tools. This deliberately does not reuse `CellModification.comment` or
 * `RangeModification.comment`: those legacy audit-comment fields are
 * ambiguous and remain disabled.
 *
 * Comments are Excel's threaded purple comments. Notes are the legacy yellow
 * sticky notes. Delete operations are batched because the host accepts one
 * `referenceAddresses[]` payload and treats missing annotations as success.
 */
export type CellAnnotationOperation =
  | {
      kind: "comment";
      action: "set";
      sheetName: string;
      address: string;
      text: string;
      replaceExisting?: boolean;
    }
  | {
      kind: "comment";
      action: "delete";
      sheetName: string;
      addresses: string[];
    }
  | {
      kind: "note";
      action: "set";
      sheetName: string;
      address: string;
      text: string;
      replaceExisting?: boolean;
      /** Internal-only marker for non-blocking generated source Notes. */
      origin?: "citation";
    }
  | {
      kind: "note";
      action: "delete";
      sheetName: string;
      addresses: string[];
    };

export interface WorkbookModification {
  sheets?: SheetModification[];
  worksheets?: WorksheetModification[];

  // Bridge-only threaded-comment / sticky-note mutations. Kept outside
  // worksheets[] so model-facing legacy cell/range comment fields cannot be
  // mistaken for either annotation family.
  annotations?: CellAnnotationOperation[];

  // Workbook-level operations that aren't tied to a single worksheet.
  // Executed after `worksheets` so they don't race per-sheet mutations.
  workbookOperations?: WorkbookOperation[];

  // Optional: message to display to user
  message?: string;

  // Verifier-only: explicit pass/fail signal. When present, the
  // orchestrator uses this instead of regex-matching the message text.
  passed?: boolean;
}

// Compatibility run step shape persisted in chat history and progress UI.
export interface PlanStep {
  step: number;
  description: string;
  action: string;  // Brief action type: "fill", "format", "formula", "table", etc.
  targetSheet?: string;  // Which sheet this step operates on (default: active sheet)
  /** Present iff `action === "apply_template"`. The agent picks
   *  templateKey via the retrieve_template tool and writes it back
   *  here so the runtime can dispatch the open directly without
   *  another LLM round-trip. companyIdentifier is the SYMBOL-COUNTRY
   *  FactSet identifier (e.g. "AAPL-US"); the runtime asks the user
   *  inline if it's missing and the template requires main_identifier. */
  applyTemplate?: {
    templateKey: string;
    companyIdentifier?: string;
    targetWorkbook?: "current" | "new";
  };
}

// Compatibility execution-plan shape used to persist run steps.
export interface ExecutionPlan {
  summary: string;
  steps: PlanStep[];
  estimatedComplexity: "simple" | "moderate" | "complex";
  /** Skill ids selected for the run. */
  selectedSkills?: string[];
}

// ============================================================================
// FACTSET PREVIEW CHART
// ============================================================================
//
// Used by the `factset.previewChart` intent (see
// `services/factset/spreadsheetIntents.ts::previewChartViaIntents` and
// `excel-docs/intent-details.md` -> "Preview Chart"). Agentic charting goes
// through `active_graph` (embedded ActiveGraph), `native_chart` (a downloaded
// Chart Creator workbook), or `range_chart` (an Excel chart over a range);
// this preview result type is retained for lower-level host wrappers and
// non-agent callers.

/** A single annotation overlay placed on a FactSet chart. */
export interface FactsetChartAnnotation {
  /** Free-text label rendered on the chart. */
  content: string;
  /** Anchor date in `MM/DD/YYYY`. */
  date: string;
  /** Optional ISO-8601 timestamp. The host accepts both `date` and
   *  `dateTime`; passing both lets the host resolve to the most
   *  precise. Annotations whose date is in the past appear in
   *  `annotationsJson.InvalidAnnotations` rather than rendering. */
  dateTime?: string;
  /** Ticker / entity the annotation attaches to (e.g. `"AAPL-US"`). */
  entity?: string;
  /** When `true`, the annotation is anchored to a peer-group entity. */
  isPeerGroup?: boolean;
}

/** Optional currency override for one of the chart's series. */
export interface FactsetChartCurrency {
  /** Display name (`"U.S. Dollar"`). */
  name: string;
  /** ISO code (`"USD"`). */
  iso: string;
  /** Whether the currency is the entity's reporting currency. */
  isEntity?: boolean;
}

/** Estimate-data parameters for estimate-driven chart types. */
export interface FactsetChartEstimateParameters {
  /** `"A"` annual or `"Q"` quarterly. */
  period?: "A" | "Q" | (string & {});
  calendarized?: string;
  estimatePeriods?: string | number;
  actualHistory?: string | number;
  statistic?: string;
  showE?: string;
  showA?: string;
}

export interface FactsetChartAxisColor {
  xAxisFGColor?: string;
  yAxisFGColor?: string;
  xFontColor?: string;
  yFontColor?: string;
  xTitleFontColor?: string;
  yTitleFontColor?: string;
  [key: string]: string | undefined;
}

export interface FactsetChartFontColor {
  legendFontColor?: string;
  heading1FontColor?: string;
  heading2FontColor?: string;
  [key: string]: string | undefined;
}

/** Format / styling overrides applied to the rendered FactSet chart. */
export interface FactsetChartFormat {
  axisColor?: FactsetChartAxisColor;
  fontColor?: FactsetChartFontColor;
  fontHeight?: number;
  heading1FontHeight?: number;
  seriesColor?: Record<string, string>;
  xGrid?: boolean;
  xLabelFormat?: string;
  yAxisPosition?: "Left" | "Right";
  yGrid?: boolean;
  yLabelPrefix?: string;
}

export type FactsetChartFrequency =
  | "D"
  | "W"
  | "M"
  | "Q"
  | "CQ"
  | "FQ"
  | "LTMQ"
  | "A"
  | "CY"
  | "FY";

export type FactsetChartHorizon = "LTM" | "NTM" | "FY1" | "FY2";

export type FactsetChartQuickLabel =
  | "None"
  | "Default"
  | "Latest"
  | "PercentChange"
  | "AbsoluteChange"
  | "Avg"
  | "Max"
  | "Min";

export type FactsetChartSummaryTableDisplay = "Horizontal" | "Vertical" | "No";

/** Every summary-table period slot value, in the order the API documents. */
export const FACTSET_CHART_SUMMARY_TABLE_PERIODS = [
  "No",
  "Current",
  "YTD",
  "L1M",
  "L3M",
  "L6M",
  "L1Y",
  "L2Y",
  "L3Y",
  "L4Y",
  "L5Y",
  "L6Y",
  "L7Y",
  "L8Y",
  "L9Y",
  "L10Y",
] as const;

export type FactsetChartSummaryTablePeriod =
  (typeof FACTSET_CHART_SUMMARY_TABLE_PERIODS)[number];

/**
 * Summary-table choices. These travel on the Chart Creator request itself,
 * under `chartParameters.summaryTable` — the API payload and the legacy
 * download body both carry them, gated by
 * `chartCreatorPayload.ts::CHART_CREATOR_SUMMARY_TABLE_API`. A standard
 * request omits the object and takes the table Chart Creator generates for
 * the chart type and date range, so only an explicit override is serialized.
 * That feature is still being rolled out, so a table sent here is reported to
 * the user as requested rather than as applied.
 */
export interface FactsetChartSummaryTable {
  display?: FactsetChartSummaryTableDisplay;
  period1?: FactsetChartSummaryTablePeriod;
  period2?: FactsetChartSummaryTablePeriod;
  period3?: FactsetChartSummaryTablePeriod;
  period4?: FactsetChartSummaryTablePeriod;
  period5?: FactsetChartSummaryTablePeriod;
}

/**
 * Shared FactSet Chart Creator parameters used by preview and workbook
 * download clients. The payload builders fill sensible defaults for
 * every truly-optional field; the two REQUIRED inputs are `tickers`
 * (non-empty array) and `chartType` (non-empty trimmed string —
 * marked `?` on the type only because the LLM-side JSON schema is
 * permissive, but the wrapper throws if it's missing/blank rather
 * than silently substituting `"P_PRICE"`).
 */
export interface FactsetChartParameters {
  /**
   * FactSet chart-type identifier. **Required** — the wrapper throws
   * if missing, empty, or whitespace-only (no silent fallback to
   * `"P_PRICE"`, since substituting one chart for another would
   * deliver a wrong-but-plausible result without telling the user).
   *
   * Validated client-side against the 27-entry FactSet ActiveGraph
   * catalog in `chartCapabilityRegistry.ts` before any host call. The
   * registry mirrors the FactSet ChartGPT skill spec
   * (`f2_docs/charting/chart-creator-api-parameter-skill.md` §1) and
   * is the same data the agent prompt is seeded with, so the prompt
   * and the gate cannot drift. Unknown codes raise a
   * `CHART_CAPABILITY_ERROR_PREFIX`-tagged error which the chat UI
   * renders verbatim as actionable feedback (e.g. "Closest available:
   * `VAL_PE2` (Trailing/LTM Price to Earnings)").
   */
  chartType?: string;
  /** Ticker IDs in `<SYMBOL>-<EXCHANGE>` form (`"AAPL-US"`). REQUIRED. */
  tickers: string[];
  /** Optional pretty company-name list, used for legend labels. */
  companyNames?: { single?: string[]; aggregated?: string[] };
  /** Chart frequency. Defaults are selected from the chart family. */
  frequency?: FactsetChartFrequency | (string & {});
  /** Relative (`"-1AY"`) or absolute (`"01/31/2025"`). Defaults to `"-1AY"`. */
  startDate?: string;
  /** `"NOW"` (default) or absolute. */
  endDate?: string;
  /** Optional rolling horizon string (estimate charts). */
  horizon?: FactsetChartHorizon | (string & {});
  /** Optional annotation overlays. */
  annotations?: FactsetChartAnnotation[];
  /** Optional FactSet styleset id. */
  stylesetId?: string;
  /** Optional math function applied to each series (`"relative_pct"`, etc.). */
  mathFunction?: string;
  /** Quick-label preset. Legacy host callers also accept numeric preset ids. */
  quickLabel?: FactsetChartQuickLabel | number;
  /** Per-series currency overrides. */
  currencies?: FactsetChartCurrency[];
  /** Estimate-chart parameters (only meaningful for `P_EPS_E`, etc.). */
  estimateParameters?: FactsetChartEstimateParameters;
  /** Format / styling overrides. */
  chartFormat?: FactsetChartFormat;
  /** Summary-table choices; see `FactsetChartSummaryTable` for how they apply. */
  summaryTable?: FactsetChartSummaryTable;
}

/**
 * Preview blob returned by `factset.previewChart` and cached on the chat
 * chart message after the first successful preview call. Drives both
 * the inline render (`pngDataUri`) and the eventual insert (`fdsChartXml`
 * → `addActiveGraph`).
 */
export interface FactsetChartPreview {
  /** PNG data URI (`data:image/png;base64,...`) when the host returned one. */
  pngDataUri?: string;
  /** Serialized FDSChart XML, fed to `addActiveGraph` on insert. */
  fdsChartXml?: string;
  /** JSON string with `{ ValidAnnotations, InvalidAnnotations }` validation. */
  annotationsJson?: string;
  /** Whether the host's styleset was applied to the chart. */
  wasStylesetChanged?: boolean;
  /** Host-reported error string, if any (preview did not render). */
  error?: string | null;
}

/** Binary Chart Creator workbook normalized to the legacy intent result shape. */
export interface FactsetDownloadExcelChart {
  /** Base64-encoded `.xlsx` bytes without a data-URI prefix. */
  workbookBase64?: string;
  /** Workbook MIME type. */
  contentType?: string;
  /** HTTP-style status from the direct API or legacy host. */
  status?: number;
  /** `null` on a successful response with workbook bytes. */
  error?: string | null;
  /** Legacy-host response escape hatch; direct API calls leave it undefined. */
  raw?: Record<string, unknown>;
}

/**
 * Result of `factset.Charting.GetActiveGraphXml` after envelope
 * unwrapping. The host returns the currently-active FactSet ActiveGraph
 * chart's serialised XML (`<FDSChart xmlns="http://xml.factset.com/chart">…</FDSChart>`)
 * — the exact same format consumed by `factset.Spreadsheet.AddActiveGraph`
 * and `factset.PreviewChart`.
 *
 * The captured XML is rich: it embeds chart metadata
 * (`FDSProperties` / `FDSAppAttributes`), every series' raw Y values
 * (`FDSData[ID="PriceData(…)"]`) plus their X dates (`FDSData[ID="X(Date)"]`),
 * the rendered chart's plot/axis settings (`FDSPlot[ID="Main Plot"]`),
 * and an `XMLTemplate` blob with the parameter set the host used to
 * regenerate the chart. **Do NOT pretend this is "just the
 * config"** — round-tripping the XML through `addActiveGraph` will
 * preserve the captured data points verbatim, which is the right
 * behaviour for export/round-trip but the wrong behaviour for
 * cross-portfolio reuse (the data won't refresh until the recipient
 * triggers a host-side refresh).
 *
 * Returned as an opaque string so the wrapper doesn't have to parse
 * (or know the FDS schema). Callers feed it back to `addActiveGraph`,
 * write it to disk for backup, hand it to a server-side parser, or
 * display it as raw XML in a dev-tool panel.
 */
export interface FactsetGetActiveGraphXmlResult {
  /** Serialised FDSChart XML for the currently-active ActiveGraph
   *  chart, or `undefined` when no chart is active / the host elided
   *  the field. The XML is the same shape `addActiveGraph` accepts on
   *  the way back in. */
  fdsChartXml?: string;
}

/**
 * One entry in the styleset catalog returned by `factset.GetStylesets`.
 * `id` is the value the chart-family intents (`PreviewChart`,
 * `DownloadExcelChart`, `DownloadActiveGraph`) accept on
 * `chartParameters.stylesetId`; `name` is the human-friendly label
 * suitable for a picker UI.
 *
 * Captured ids include `fds_classic`, `fds_default`, `fds_basic`,
 * `PA_Carbon`, `PA_QUARTZ`, `PA_Legacy`, `PA_PEARL` — note the mixed
 * casing (`fds_*` vs `PA_*`); pass through verbatim.
 */
export interface FactsetStyleset {
  id: string;
  name: string;
}

/**
 * Result of `factset.GetStylesets` after envelope unwrapping. Lists the
 * stylesets available to the host and which one is the user's current
 * default. Used by chart pickers to populate the "Style" dropdown and
 * to highlight the active selection.
 */
export interface FactsetGetStylesetsResult {
  /** Available stylesets, in host order. */
  stylesets: FactsetStyleset[];
  /** The host's currently-active styleset id (matches one of
   *  `stylesets[].id`), or `undefined` when the host did not report one. */
  currentStylesetId?: string;
}

/**
 * One input parameter the host expects when instantiating a template
 * via the FactSet template-library workflow. `inputType` is a string
 * enum on the wire — captured values include `"main_identifier"`,
 * `"start_date"`, `"end_date"`, `"currency"`, `"frequency"`. Treat as
 * an open-ended string so a future host extension lands without a
 * wrapper update.
 */
export interface FactsetTemplateInput {
  inputType: string;
}

/**
 * Per-template metadata returned by `factset.GetTemplatesList`. Carries
 * the input parameters the template asks for and an optional
 * `identifierGroups` blob the host uses for templates that take
 * multi-identifier slots (e.g. peer-comp templates with `nbEntries`-
 * sized groups like `{ "group1": { "nbEntries": 9 } }`).
 */
export interface FactsetTemplateMetadata {
  /** Input parameters the template asks for at instantiation time. */
  inputs?: FactsetTemplateInput[];
  /** Open-ended map of identifier-group definitions. Captured shape is
   *  `{ "group1": { "nbEntries": N }, ... }`; surfaced as
   *  `Record<string, unknown>` for forward-compat with future host
   *  fields. */
  identifierGroups?: Record<string, unknown>;
}

/**
 * One entry in the FactSet template library returned by
 * `factset.GetTemplatesList`. Consumers typically use `name` /
 * `description` for picker UI, `category` to group entries,
 * `templateUrl` to fetch the underlying `.xlsx` binary, `previewUrl`
 * to render a PDF preview (`null` on entries the host doesn't ship a
 * preview for), and `metadata.inputs` to know which inputs to collect
 * from the user before passing the template into a downstream
 * workflow.
 *
 * **Naming caveat — `key` is a wire-side field, not a JS object key.**
 * The captured payload includes a `key` field carrying the lowercased
 * filename of the underlying `.xlsx` (e.g. `"public company profile.xlsx"`).
 * Because `key` is also a generic-looking name, surface it as the
 * literal field name and document it inline; consumers should treat
 * it as the host's stable per-template identifier rather than the
 * `name` (which can include trailing whitespace and human styling).
 */
export interface FactsetTemplate {
  /** Folder-style category path, e.g. `"Company Overview/"`,
   *  `"Financials ＆ Estimates/Standardized Financials/"`. The
   *  fullwidth `＆` (U+FF06) is intentional on the wire — preserve
   *  it verbatim if the picker UI groups by category string. */
  category: string;
  /** Opaque cache validator for change detection across refreshes. */
  etag: string;
  /** Last-modified timestamp (ISO-8601). */
  modtime: string;
  /** Template content language tag, e.g. `"en-US"`, `"ja"`,
   *  `"zh-Hant"`. Useful for filtering by user locale. */
  language: string;
  /** Lower-cased filename of the underlying `.xlsx`. Stable per-
   *  template identifier — prefer over `name` for keys, dedup, or
   *  cross-refresh diffs. */
  key: string;
  /** URL to download the template `.xlsx` bytes. */
  templateUrl: string;
  /** URL to a PDF preview, or `null` when the host has no preview for
   *  this template (e.g. several `Industry/Banks/` entries). */
  previewUrl: string | null;
  /** Human-friendly template name (typically what a picker UI shows). */
  name: string;
  /** Short one-line description suitable for a picker tooltip. */
  description: string;
  /** Long-form description — often LLM-generated, often Markdown,
   *  often multi-KB. Loaded eagerly today; a future iteration could
   *  defer fetching this for a slimmer wire payload, but the host
   *  ships it inline so the wrapper passes it through. */
  extendedDescription: string;
  /** Free-text keywords for search; often empty on the captured trace. */
  searchKeywords: string[];
  /** Input requirements + identifier-group hints. */
  metadata: FactsetTemplateMetadata;
  /** Carry any other fields verbatim — the host catalog may grow
   *  (new metadata fields, localized name variants, etc.) and the
   *  wrapper shouldn't lose data the caller might want to inspect. */
  [extra: string]: unknown;
}

/**
 * Result of `factset.GetTemplatesList` after envelope unwrapping.
 * Lists the templates the host's template library exposes, suitable
 * for populating a template picker, library browser, or AI agent
 * that pre-fills an `.xlsx` from a user prompt.
 */
export interface FactsetGetTemplatesListResult {
  /** All templates the host returned, in host order. */
  templates: FactsetTemplate[];
}

/**
 * Result of `factset.GetUserAvailableTemplates` after envelope
 * unwrapping. The host returns the **subset of template keys the
 * current user is entitled to** — a flat list of lowercased `.xlsx`
 * filenames (e.g. `"public company profile.xlsx"`,
 * `"banks - quick comps.xlsx"`).
 *
 * **Sibling-intent pairing.** Each entry in `templateKeys` is exactly
 * a value from `FactsetTemplate.key` returned by `getTemplatesList`.
 * Use this intent to filter / gate the catalog from
 * `getTemplatesList` to just the entries the user can actually
 * instantiate — pseudo-code:
 *   `templates.filter(t => userAvailable.templateKeys.includes(t.key))`.
 *
 * Treat the field name as `templateKeys` rather than `keys` so the
 * pairing with `FactsetTemplate.key` is obvious at the call site
 * (and so a future host extension that adds entitlement metadata
 * past the bare key — e.g. `[ { key, expiresAt } ]` — can land on a
 * different field without breaking this one).
 */
export interface FactsetGetUserAvailableTemplatesResult {
  /** Lowercased `.xlsx` filenames the current user is entitled to,
   *  in host order. Each value matches one `FactsetTemplate.key`
   *  from `getTemplatesList`. Host order appears to mirror
   *  `getTemplatesList` order on the captured trace; do not assume
   *  alphabetical sort. */
  templateKeys: string[];
}

/**
 * Request shape for `factset.DownloadTemplate`. The captured wire
 * payload is the bare context object — no stringified `body`, no
 * `data:` envelope — just `{ type: "factset.downloadTemplate", key }`.
 *
 * **Pairing with `getTemplatesList`.** `key` is exactly a value from
 * `FactsetTemplate.key` returned by `getTemplatesList` (the lowercased
 * `.xlsx` filename — e.g. `"public company profile.xlsx"`). The
 * typical flow is:
 *
 *   1. `getTemplatesList()` → catalog of `FactsetTemplate[]`.
 *   2. (Optional) `getUserAvailableTemplates()` → entitlement filter.
 *   3. `downloadTemplate({ key })` → raw `.xlsx` bytes for that
 *      template, **without instantiating it**. Use `refreshCalculate`
 *      instead if you want the host to fill in the template's inputs
 *      first.
 */
export interface FactsetDownloadTemplateRequest {
  /** Lowercased `.xlsx` filename of the template to download.
   *  Must match a `FactsetTemplate.key` from `getTemplatesList` —
   *  ideally one that also appears in
   *  `getUserAvailableTemplates().templateKeys` for this user.
   *  Surfaced to the host as a top-level field on the context
   *  object (NOT inside a `body` blob like `RefreshCalculate`). */
  key: string;
}

/**
 * Result of `factset.DownloadTemplate` after envelope unwrapping.
 * Mirrors `FactsetDownloadExcelChart` / `FactsetDownloadActiveGraph`
 * — same fields, same coercion rules, same `error`-synthesis
 * semantics. The bytes are surfaced as `workbookBase64` for naming
 * symmetry with the other download wrappers, even though the raw
 * template `.xlsx` carries neither a native chart nor an OLE embed.
 *
 * **What's actually inside the bytes.** Unlike the chart-family
 * downloads (`DownloadExcelChart` / `DownloadActiveGraph`), the
 * workbook returned here is the **template itself** — the same
 * `.xlsx` the host would hand off to `RefreshCalculate` for
 * instantiation, before any input values have been filled in. Open
 * the bytes to inspect the template's structure, hand them to a
 * file-save UI, or feed them into a downstream pipeline that
 * doesn't need server-side computation.
 *
 * **Captured shape (2026-05-05).** Response envelope is the flatter
 * one-level form (same as `DownloadExcelChart` / `DownloadActiveGraph`):
 *
 *   { type: "factset.common.downloadTemplateResult",
 *     data: { status: 200,
 *             contentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
 *             bodyAsBase64: "UEsDBBQ…" } }
 *
 * (`bodyAsBase64` starts with `UEsDBBQ` — PK ZIP magic — confirming
 * a real Open-XML workbook on the wire.)
 */
export interface FactsetDownloadTemplate {
  /** Base64-encoded `.xlsx` bytes (no `data:` prefix). Mapped from
   *  the wire-side `data.bodyAsBase64`. Suitable for
   *  `<a download href="data:…;base64,…">` flows or for
   *  `factset.Spreadsheet.InsertWorksheetsFromBase64` pipelines (the
   *  host primitive copies worksheet cell data only — fine for a
   *  template that doesn't carry chart drawings or OLE embeds). */
  workbookBase64?: string;
  /** MIME type the host returned —
   *  `"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"`
   *  for `.xlsx`. Mapped from the wire-side `data.contentType`. */
  contentType?: string;
  /** Numeric HTTP-style status the host reported (200 on success). */
  status?: number;
  /** Synthesised error message — same semantics as
   *  `FactsetDownloadExcelChart.error`:
   *    - non-200 status → `"Host returned status N"`
   *    - 200 with no bytes → `"Host returned status 200 but no bodyAsBase64"`
   *    - 200 with bytes → `null` (success)
   *    - no status, bytes present → `undefined` (treat as success). */
  error?: string | null;
  /** Raw inner body — defensive escape hatch for callers that want
   *  to introspect fields the wrapper hasn't taught itself about. */
  raw?: Record<string, unknown>;
}

/**
 * Inputs map for a `factset.RefreshCalculate` request. Keys are the
 * `inputType` strings advertised on
 * `FactsetTemplate.metadata.inputs[].inputType` (captured values:
 * `"main_identifier"`, `"start_date"`, `"end_date"`, `"currency"`,
 * `"frequency"`); values are the user-supplied input values as
 * strings. The host accepts only string values today (the captured
 * payload is `"main_identifier":"AC-FR","currency":"EUR"`); if the
 * host grows numeric / date input types later, widen this to
 * `string | number` rather than coercing on the client.
 *
 * Open-ended map shape so a future host extension that introduces a
 * new `inputType` lands without a wrapper update.
 */
export interface FactsetRefreshCalculateInputs {
  [inputType: string]: string;
}

/**
 * Request shape for `factset.RefreshCalculate`. Matches the
 * `data: { key, inputs }` envelope the host expects on the
 * stringified `body` field.
 */
export interface FactsetRefreshCalculateRequest {
  /** Lowercased `.xlsx` filename of the template to instantiate.
   *  Must match a `FactsetTemplate.key` from `getTemplatesList` —
   *  ideally one that also appears in
   *  `getUserAvailableTemplates().templateKeys` for this user. */
  key: string;
  /** Input values keyed by `inputType`. Provide one entry per
   *  required `FactsetTemplate.metadata.inputs[].inputType`; extra
   *  / unknown keys are forwarded to the host verbatim (the host
   *  rejects them if it doesn't recognise them). */
  inputs: FactsetRefreshCalculateInputs;
}

/**
 * Result of `factset.RefreshCalculate` after envelope unwrapping.
 *
 * **Asynchronous semantics.** The host returns immediately with
 * `httpStatus: 202` and a job `id` — the actual template
 * instantiation runs server-side and finishes later. The captured
 * `status` value is `"queued"`; `"running"` / `"completed"` /
 * `"failed"` are plausible terminal states the host has not yet
 * surfaced on the captured trace, so treat the field as an opaque
 * string rather than a closed enum.
 *
 * **Polling + retrieval.** Round-trip `id` through:
 *   - `getRefreshStatus` / `awaitRefreshCalculate` for status polling
 *   - `getRefresh` for result payload retrieval (bytes when ready,
 *     status payload otherwise).
 */
export interface FactsetRefreshCalculateResult {
  /** Server-assigned job id (UUID). Use to poll status / fetch the
   *  finished workbook through `getRefreshStatus` / `getRefresh`. */
  id: string;
  /** Job status string from `body.data.status`. Captured value:
   *  `"queued"`. Treat as opaque — `"running"` / `"completed"` /
   *  `"failed"` are plausible terminal values the host has not yet
   *  surfaced on the captured trace. */
  status: string;
  /** HTTP-style status code from the response envelope (`data.status`).
   *  Captured value: `202` (Accepted — request queued for async
   *  processing). Surfaced because async semantics make the 200-vs-202
   *  distinction meaningful: a future host build that completes
   *  synchronously would return `200` with a richer `data` payload.
   *  Wrappers should treat any 2xx as success. */
  httpStatus?: number;
}

/**
 * Request shape for `factset.GetRefresh`. This intent fetches the
 * refresh-result payload for a prior async refresh job.
 *
 * Most callers pass `refreshId` from `FactsetRefreshCalculateResult.id`.
 * Some host builds also accept an `orUrl` override that points at the
 * Office-refresh URL directly.
 */
export interface FactsetGetRefreshRequest {
  /** Refresh job id from `refreshCalculate` / `getRefreshStatus`. */
  refreshId?: string;
  /** Optional Office-refresh URL override (host-specific). */
  orUrl?: string;
}

/**
 * Result of `factset.GetRefresh` after envelope unwrapping.
 *
 * The host can return either:
 * - workbook bytes (`status: 200`, `bodyAsBase64` payload), or
 * - an in-progress refresh status payload (`status: 202` / `201` with
 *   a JSON body describing `{ id, status }`).
 *
 * This wrapper surface keeps both shapes so callers can decide whether
 * to keep polling (`refreshStatus`) or consume workbook bytes
 * (`workbookBase64`).
 */
export interface FactsetGetRefreshResult {
  /** Base64-encoded workbook bytes when the refresh result is ready. */
  workbookBase64?: string;
  /** MIME type of the returned payload (typically `.xlsx`). */
  contentType?: string;
  /** HTTP-style status code from the host envelope. */
  status?: number;
  /** Parsed status payload when the host returns JSON status instead of bytes. */
  refreshStatus?: {
    id?: string;
    status?: string;
    [key: string]: unknown;
  };
  /** Synthesised error for non-2xx / malformed success payloads. */
  error?: string | null;
  /** Raw inner payload for diagnostics / forward-compat probing. */
  raw?: Record<string, unknown>;
}

// ============================================================================
// CHAT TYPES (Reusing PowerPoint patterns)
// ============================================================================

export type ExcelStepStatus =
  | "success"
  | "warning"
  | "failed"
  | "queued"
  | "recovered"
  | "paused";

// ============================================================================
// MULTIHOP EXECUTOR PLAN STATE
// ============================================================================

/**
 * Live step status used by the multi-hop executor agent during a run.
 * Richer than `ExcelStepStatus` to distinguish active phases (running vs
 * verifying). Mapped to `ExcelStepStatus` at the chat-history persistence
 * boundary so persisted messages remain backward-compatible.
 */
export type ExecutorStepStatus =
  | "queued"
  | "running"
  | "verifying"
  | "success"
  | "warning"
  | "failed"
  | "recovered"
  | "paused";

/**
 * Per-step state within the executor's mutable run-plan. Each entry
 * tracks a single plan step's lifecycle from queued through terminal.
 */
export interface ExecutorStepState {
  /** Stable identifier for this step (survives insert/remove/reorder). */
  id: string;
  /** The underlying plan step definition (description, action, etc.). */
  planStep: PlanStep;
  /** Current live status of this step. */
  status: ExecutorStepStatus;
  /** Optional note from the agent (e.g. why it failed, what it fixed). */
  note?: string;
}

/**
 * Mutable step state owned by the multi-hop executor agent. Initialized
 * from optional compatibility `ExecutionPlan` input (as a copy — the original is never
 * mutated) and updated via `set_step_status` / `revise_plan` tool calls.
 * Propagated to the UI live and persisted as the final plan in chat history.
 */
export interface ExecutorPlanState {
  /** Plan summary (may be revised by the agent mid-run). */
  summary: string;
  /** Complexity selected for verification budgeting. */
  estimatedComplexity?: ExecutionPlan["estimatedComplexity"];
  /** Ordered list of steps with their live statuses. */
  steps: ExecutorStepState[];
  /** ID of the step currently being executed, if any. */
  currentStepId?: string;
}

export interface ExecutorRunCheckpoint {
  /** Schema version for persisted continuation compatibility checks. */
  version?: 1;
  plan: ExecutionPlan;
  stepStates: ExecutorStepState[];
  completedStepIds: string[];
  terminalStepIds: string[];
  currentStepId?: string;
  stepResults: string[];
  stepResultIndexById: Array<[string, number]>;
  stepDisplayResults: string[];
  stepStatuses: ExcelStepStatus[];
  lastModifications: WorkbookModification | null;
  editedCells: string[];
  formulaWriteTargets: Array<{ sheetName: string; address: string }>;
  fdsFormulaWriteTargets: Array<{ sheetName: string; address: string }>;
  /** Confirmed FDS formula-write count keyed by normalized `sheet!cell`. */
  fdsFormulaWriteAttempts?: Record<string, number>;
  /** Exact FDS cells verified unavailable after correction + refresh/recalc. */
  unavailableFdsFormulaTargets?: Array<{ sheetName: string; address: string }>;
  fdsDateAxisWriteTargets: Array<{
    sheetName: string;
    address: string;
    kind: "serial" | "key";
  }>;
  autofitSheetNames: string[];
  autofitTargets: Array<{ sheetName: string; address: string }>;
  /** Per-column text evidence used to size narrative columns in the final pass. */
  columnSizingHints?: import("./columnSizingPolicy").ColumnSizingHint[];
  /** Address-keyed evidence used to replace sizing observations after rewrites. */
  columnSizingCellEvidence?: import("./columnSizingPolicy").ColumnSizingCellEvidenceMap;
  /** Columns sized explicitly this run, which automatic sizing must not override. */
  explicitColumnWidthKeys?: import("./columnSizingPolicy").ColumnSizingKey[];
  fdsRefreshSheets: string[];
  deferredRefreshAllRequested: boolean;
  deferredRecalcMode: "recalculate" | "full" | "fullRebuild" | null;
  workbookEditAttempted: boolean;
  /** Successful ordinary or verified tabular research credited to this run. */
  successfulResearchCallCount?: number;
  /** Whether the current persisted tabular workload already contributed once. */
  tabularResearchSuccessCounted?: boolean;
  executedWriteFingerprints: string[];
  /**
   * Confirmed non-idempotent writes retained only to require fresh workbook
   * inspection before an argument-identical operation is attempted after
   * continuation. Unlike `executedWriteFingerprints`, these do not permanently
   * suppress intentional repeats within a run.
   */
  confirmedRepeatableWriteFingerprints?: string[];
  /** Granular range writes whose single readback-first recovery was consumed. */
  granularRecoveryKeys?: string[];
  /** Successful generate_formula entries keyed by exact query text. */
  formulaResolutionCache?: Array<[string, Record<string, unknown>]>;
  /** Per-query TTF dispatch counts, including the initial attempt. */
  formulaResolutionAttempts?: Array<[string, number]>;
  /** Forward-P/E chart fidelity evidence preserved across continuations. */
  forwardPeChartVerification?: import("./forwardPeChartGuard").ForwardPeChartVerificationState;
  /** JSON-safe state for an interrupted aligned tabular-query workload. */
  tabularQueryWorkload?: import("./tabularQueryWorkload").TabularQueryWorkloadCheckpoint;
  /**
   * Schema version of the research evidence held for this operation, and a
   * digest of the policy it was collected under.
   *
   * Only these two markers are persisted. Answers, citations, and guidance stay
   * in the in-memory operation registry: this checkpoint is written to
   * `sessionStorage`, which is neither private enough nor large enough for
   * research payloads. On resume they prove the surviving pack belongs to this
   * operation's policy before any of it is applied.
   */
  evidenceVersion?: number;
  evidencePolicyFingerprint?: string;
  blockedOperation?: {
    toolName: string;
    args: unknown;
    stepId?: string;
    reason?: string;
    confirmedEditedCells?: string[];
    readbackObserved?: boolean;
    unresolvedCells?: string[];
    inFlightCells?: string[];
  };
  verification: {
    requiresVerification: boolean;
    refreshRequired: boolean;
    recalcRequired: boolean;
    refreshObserved: boolean;
    recalcObserved: boolean;
    cleanReadbackAfterRecalc: boolean;
    hardErrors: import("./excelHardErrors").ExcelHardErrorCell[];
    pendingReadbackSheetNames: string[];
    verifiedReadbackSheetNames: string[];
    pendingReadbackTargets: Array<{ sheetName: string; address: string }>;
    verifiedReadbackTargets: Array<{ sheetName: string; address: string }>;
    pendingContentReadbackTargets: Array<{
      sheetName: string;
      address: string;
      rowCount: number;
      columnCount: number;
      cells: Array<{
        address: string;
        kind: "formula" | "value" | "blank";
        expected: string | number | boolean | null;
      }>;
    }>;
    verifiedContentReadbackTargetKeys: string[];
    contentReadbackFailures: Array<{ key: string; reason: string }>;
    pendingShapeCorrections: Array<{
      toolName: string;
      stepId: string | null;
      sheetName?: string;
      address: string;
      matrixField?: "values" | "formulas";
      attempted: boolean;
    }>;
    reminderCount: number;
  };
}

/**
 * Normalized output of any executor implementation (legacy step loop or
 * multi-hop agent). Wraps the same local variables the shared tail in
 * `handleQuery` already reads from, so both paths produce this and the
 * finalize + chat-message code stays unchanged.
 */
export interface ExecutorRunResult {
  mode?: "workbook_edit" | "partial" | "failed";
  /** Concise user-facing final reply for the main assistant bubble. */
  reply?: string;
  retrievedSources?: import("./agentTimeline").ExcelAgentSource[];
  retrievedCitations?: unknown[];
  retrievedCitedValuesByUuid?: Record<string, string>;
  /** Final plan (original for legacy, possibly revised for multi-hop). */
  plan: ExecutionPlan;
  /** Internal per-step execution log lines (used for gap detection). */
  stepResults: string[];
  /** User-facing per-step summary text, index-aligned with plan.steps. */
  stepDisplayResults: string[];
  /** Per-step status icons, index-aligned with plan.steps. */
  stepStatuses: ExcelStepStatus[];
  /** Last applied modification payload (shown in chat history). */
  lastModifications: WorkbookModification | null;
  /** All edited cell/range addresses from successful apply calls. */
  editedCells: string[];
  /** Concrete formula-write ranges that need a final hard-error read-back. */
  formulaWriteTargets: Array<{ sheetName: string; address: string }>;
  /** Worksheets touched by successful writes and eligible for one final autofit pass. */
  autofitSheetNames: Set<string>;
  /** Concrete content-write ranges whose columns are eligible for final autofit. */
  autofitTargets: Array<{ sheetName: string; address: string }>;
  /** Per-column text evidence used to size narrative columns in the final pass. */
  columnSizingHints?: import("./columnSizingPolicy").ColumnSizingHint[];
  /** Columns sized explicitly this run, which automatic sizing must not override. */
  explicitColumnWidthKeys?: import("./columnSizingPolicy").ColumnSizingKey[];
  /** Sheets that received FDS-bound formulas and need refresh. */
  fdsRefreshSheets: Set<string>;
  /** Whether any workbook operation requested refresh-all. */
  deferredRefreshAllRequested: boolean;
  /** Deferred recalc mode requested by workbook operations. */
  deferredRecalcMode: "recalculate" | "full" | "fullRebuild" | null;
  /** Sheet captured at run start for safe apply fallback. */
  sessionLockSheet: string;
  /** Whether any apply was attempted (for analytics). */
  workbookEditAttempted: boolean;
  /**
   * When set, the shared finalize tail should skip the final verification
   * and post-plan refresh pass. Produced by `apply_template` warning-stop
   * resolution in the multi-hop path.
   */
  skipFinalVerificationReason?: string;
  /**
   * Final pinned workbook name at end of run. May differ from the initial
   * pin if `apply_template` opened a new workbook mid-run. The shared
   * finalize tail uses this for `finalizeRefreshAndRecalc`.
   */
  finalPinnedWorkbookName?: string;
  /** Aggregate read-back audit for citation Notes written during this run. */
  sourceNoteAudit?: import("./citationNotes").SourceNoteAudit;
  /** Serializable continuation state for partial, failed, or interrupted runs. */
  checkpoint?: ExecutorRunCheckpoint;
  /**
   * Set when a resumed run finished without landing a single new edit. Another
   * continuation would replay the same dead end, so the UI must not offer one.
   */
  continuationExhausted?: boolean;
}

export interface ExecutorAnswerRunResult {
  mode: "answer" | "clarification" | "failed";
  reply: string;
  thinking?: string;
  retrievedSources?: import("./agentTimeline").ExcelAgentSource[];
  retrievedCitations?: unknown[];
  retrievedCitedValuesByUuid?: Record<string, string>;
  workbookEditAttempted: false;
}

export type ExecutorUnifiedRunResult = ExecutorRunResult | ExecutorAnswerRunResult;

export interface ExcelScopedRetryPayload {
  modifications: WorkbookModification;
  targetSheet?: string;
  workbookName?: string;
}

export interface ExcelPausedPlanPayload {
  originalQuery: string;
  plan: ExecutionPlan;
  blockedStepIndex: number;
  blockedModifications?: WorkbookModification;
  stepResults: string[];
  stepDisplayResults: string[];
  stepStatuses: ExcelStepStatus[];
  editedCells: string[];
  fdsRefreshSheets: string[];
  deferredRefreshAllRequested: boolean;
  deferredRecalcMode: "recalculate" | "full" | "fullRebuild" | null;
  executedFingerprints: string[];
  sessionLockSheet: string;
  workbookKey: string;
  workbookName?: string;
  lastModifications?: WorkbookModification;
  /** Complete ReAct runner state used by the production continuation path. */
  checkpoint?: ExecutorRunCheckpoint;
  /**
   * Opaque handle for this operation's user-Skill binding. The context itself
   * is held in memory only; this payload is persisted, so it must carry no
   * guidance, names, or slugs.
   */
  skillContinuationId?: string;
  /**
   * Whether the original submission ran under a user Skill. Lets a retry after
   * a task-pane reload detect that its binding is gone and refuse rather than
   * silently continue without the Skill's policy.
   */
  skillBound?: boolean;
  /**
   * Account this plan was paused under. The in-memory operation registry is
   * emptied on an account change and by a task-pane reload, and a resume with
   * no frozen record is allowed precisely so a reload can continue its own
   * work. This is what tells those two cases apart. Optional because a payload
   * written by an earlier build can still be in this session's storage.
   */
  accountScopeKey?: string | null;
}

export interface ExcelChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  modifications?: WorkbookModification;
  thinking?: string;
  /** Persisted capped agent timeline for replaying completed Thought process. */
  agentTimeline?: ExcelAgentTimelineState;
  /** Sources retrieved by the research tool during this assistant turn. */
  retrievedSources?: ExcelAgentSource[];
  /** Raw citation payloads from the research tool, kept for future source dialogs. */
  retrievedCitations?: unknown[];
  /** Optional cited UUID -> displayed value map when the model provides one. */
  retrievedCitedValuesByUuid?: Record<string, string>;
  plan?: ExecutionPlan;
  stepResults?: string[];
  stepStatuses?: ExcelStepStatus[];
  /**
   * Inline action buttons rendered below the message content. Each
   * action's `reply` is fed back through `handleQuery()` exactly as
   * if the user had typed it, so any regex/keyword routing the chat
   * input already does (`templateIdentifierConflictChoice`,
   * `queryRequestsNewWorkbook`, the answer-vs-add keyword matcher,
   * etc.) keeps working without a parallel button-handling code path.
   * Buttons should be designed so `reply` reads well as the user's
   * own chat message — it is echoed verbatim into the message log.
   */
  actions?: ExcelChatMessageAction[];
}

export interface ExcelChatMessageAction {
  /** React key + log/test selector. */
  id: string;
  /** Visible button text. */
  label: string;
  /**
   * String passed to `handleQuery()` on click for normal reply actions.
   * MUST satisfy whichever
   * routing predicate the assistant message expects (e.g. for the
   * Identifier-conflict prompt, the reply must match either
   * `queryRequestsNewWorkbook` or `templateIdentifierConflictChoice`'s
   * "replaceExisting" arm). Keep it readable — it appears in chat
   * history as if the user typed it. Omitted for scoped retry actions.
   */
  reply?: string;
  /**
   * Scoped workbook modifications to retry directly. Used for
   * busy/edit-mode recovery buttons so the click does not replay the
   * entire original user query and duplicate steps that already landed.
   */
  retryPayloads?: ExcelScopedRetryPayload[];
  /**
   * Paused plan state to resume after Excel leaves edit/busy mode. Unlike
   * `retryPayloads`, this continues the remaining plan steps after retrying
   * the blocked step.
   */
  pausedPlanPayload?: ExcelPausedPlanPayload;
  /** Visual style. Defaults to "default". */
  variant?: "default" | "outline";
}

export interface ExcelChatSession {
  id: string;
  name: string;
  messages: ExcelChatMessage[];
  /** Ordinary research sources selected for this session's future runs. */
  selectedDataSources?: FinsterQueryDataSource[];
  /** User-uploaded file IDs selected for this session's future research runs. */
  selectedUserFileIds?: string[];
  /** Whether this session uses defaults, an explicit source filter, or files. */
  researchSelectionMode?: "default" | "filtered" | "user_files";
  sheetName: string; // Associated worksheet
  createdAt: number;
  updatedAt: number;
}

/**
 * The operation a tab is waiting on an answer for.
 *
 * A request, the clarification replies that follow it, and any paused-plan
 * resumes are one operation. Carrying the operation id here is what lets a
 * reply reuse the policy the operation was started under instead of resolving
 * Skills again against a catalog that may have changed in between.
 *
 * Transient like the rest of the in-flight tab fields: stripped before the tab
 * is persisted, because the Skill context it points at lives only in memory.
 */
export interface ExcelPendingOperation {
  /** Shared with `ExcelPausedPlanPayload.skillContinuationId`. Opaque. */
  continuationId: string;
  /** The original request. The user's reply is only a routing hint. */
  originalQuery: string;
  /**
   * Whether the operation resolved to a Skill. A reply that claims a binding we
   * can no longer produce is refused rather than answered without the Skill.
   */
  skillBound: boolean;
  /**
   * Account the operation was started under. Always known here, because this
   * field never leaves memory, and validated with the same gate the persisted
   * paused-plan payload goes through so both kinds of continuation prove the
   * same thing.
   */
  accountScopeKey: string | null;
}

export interface ExcelTab {
  id: string;
  session: ExcelChatSession;
  isLoading: boolean;
  loadingStatus: string;
  /** Per-tab progress state so background runs update only their workbook. */
  currentIteration: number;
  currentPlan: ExecutionPlan | null;
  /** Structured PowerPoint-style live Thought process timeline. */
  agentTimeline?: ExcelAgentTimelineState;
  /** Live source cards gathered from research tool results. */
  retrievedSources?: ExcelAgentSource[];
  /** Live raw citation envelopes used by the shared citation popup. */
  retrievedCitations?: unknown[];
  /** Live cited UUID -> displayed value map when available. */
  retrievedCitedValuesByUuid?: Record<string, string>;
  result: { success: boolean; message: string } | null;
  /** Set while an agent clarification is awaiting the user's reply. */
  pendingOperation?: ExcelPendingOperation;
}

export interface ExcelTabsState {
  tabs: ExcelTab[];
  activeTabId: string;
}

// Helper to generate unique IDs
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ============================================================================
// STREAM CALLBACKS
// ============================================================================

export interface ExcelStreamCallbacks {
  onThinking: (text: string) => void;
  onComplete: (modifications: WorkbookModification) => void;
  onError: (error: string) => void;
}
